import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { AI_BUILTIN_MODELS } from '@/constants/aiModels'
import {
  aiChatApi,
  type AiAvailableModel,
  type AiCustomModelView,
  type AiModelSource,
  type CreateCustomModelPayload,
  type TestCustomModelPayload,
  type TestCustomModelResult,
  type UpdateCustomModelPayload
} from '@/api/aiChat'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'

const STORAGE_KEY = 'smartcampus-ai-model-prefs'

interface PersistedModelPrefs {
  selectedApiId?: string | null
  /** 仅当用户在下拉里「显式」选择过模型时为 true；系统默认/回落不写该标记。 */
  explicit?: boolean
}

function loadPersistedPrefs(): PersistedModelPrefs {
  if (typeof localStorage === 'undefined') {
    return {}
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return {}
    }
    return (JSON.parse(raw) as PersistedModelPrefs) ?? {}
  } catch {
    return {}
  }
}

/** 仅持久化用户「显式」选择的模型；系统默认模型的回落不落本地，运营改默认后才能即时生效。 */
function persistExplicitChoice(apiId: string): void {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedApiId: apiId, explicit: true }))
  } catch {
    /* ignore */
  }
}

export const useAiModelPreferenceStore = defineStore('aiModelPreference', () => {
  const persisted = loadPersistedPrefs()
  // 用户级偏好：仅「显式」选择过的模型 apiId 才算偏好（类似主题，跨租户保留）。
  // 旧版本会把系统回落的默认值也写进本地，这里用 explicit 标记区分；未标记的按“无显式偏好”处理，
  // 于是运营改默认模型后，老用户下次进入也会跟随新默认。
  const explicitApiId = ref<string | null>(
    persisted.explicit === true ? persisted.selectedApiId ?? null : null
  )
  // 生效选择：初始取显式偏好，否则暂用内置兜底，待服务端清单加载后由 resolveSelection 收敛到默认模型。
  const selectedApiId = ref<string | null>(explicitApiId.value ?? AI_BUILTIN_MODELS[0]?.apiId ?? null)
  // 租户级状态：服务端按租户/用户返回的可用模型清单（含官方 + 自定义）。
  const serverModels = ref<AiAvailableModel[]>([])
  // 自定义模型元数据（含 id / deletable，供删除按钮使用）。按 apiId 与 serverModels 对齐。
  const customModels = ref<AiCustomModelView[]>([])
  const modelsLoaded = ref(false)

  /** 合并官方与自定义模型后的可选项，带来源徽标与删除元数据。 */
  const allOptions = computed(() => {
    type Row = {
      apiId: string
      label: string
      beta: boolean
      online: boolean
      limited: boolean
      builtin: boolean
      source: AiModelSource
      /** 自定义模型的后端主键，用于删除；官方模型为 null */
      customId: number | null
      /** 当前用户是否可删除该自定义模型 */
      deletable: boolean
    }
    const fallback: Row[] = AI_BUILTIN_MODELS.map((m) => ({
      apiId: m.apiId,
      label: m.label,
      beta: m.beta ?? false,
      online: m.online ?? false,
      limited: false,
      builtin: true,
      source: 'OFFICIAL' as AiModelSource,
      customId: null,
      deletable: false
    }))
    const customByApiId = new Map<string, AiCustomModelView>()
    for (const c of customModels.value) {
      customByApiId.set(c.apiId, c)
    }
    const available: Row[] = serverModels.value.map((m) => {
      const source: AiModelSource = m.source ?? 'OFFICIAL'
      const custom = customByApiId.get(m.apiId)
      return {
        apiId: m.apiId,
        label: m.label,
        beta: false,
        online: true,
        limited: m.limited,
        builtin: source === 'OFFICIAL',
        source,
        customId: custom?.id ?? null,
        deletable: custom?.deletable ?? false
      }
    })
    const seen = new Set<string>()
    const merged: Row[] = []
    for (const row of (available.length > 0 ? available : fallback)) {
      const k = row.apiId.toLowerCase()
      if (!row.apiId || seen.has(k)) continue
      seen.add(k)
      merged.push(row)
    }
    return merged
  })

  const selectedLabel = computed(() => {
    const id = selectedApiId.value
    if (!id) return AI_BUILTIN_MODELS[0]?.label ?? '模型'
    const hit = allOptions.value.find((o) => o.apiId === id)
    return hit?.label ?? id
  })

  /** 运营在控制台设置的默认官方模型（后端 defaultModel 标记）；无则 null。 */
  const serverDefaultApiId = computed(
    () => serverModels.value.find((model) => model.defaultModel)?.apiId ?? null
  )

  /**
   * 收敛生效选择：显式偏好（仍在可用列表中）> 服务端默认模型 > 首个可选项。
   *
   * <p>运营改默认模型后，未显式选过模型的用户下次进入即跟随新默认；显式选过的用户保留其选择，
   * 直到该模型不再可用（停用/删除）才回落到默认。避免把失效 apiId 发给后端导致调用失败。</p>
   */
  function resolveSelection(): void {
    const options = allOptions.value
    if (options.length === 0) {
      selectedApiId.value = null
      return
    }
    const explicit = explicitApiId.value
    if (explicit && options.some((option) => option.apiId === explicit)) {
      selectedApiId.value = explicit
      return
    }
    const preferred = serverDefaultApiId.value
    selectedApiId.value =
      preferred && options.some((option) => option.apiId === preferred)
        ? preferred
        : options[0].apiId
  }

  // 可用清单变化（首次加载 / 切换租户 / 增删模型）时重新收敛选择。
  watch(allOptions, () => resolveSelection(), { immediate: true })

  /** 用户在下拉里显式选择模型：记为偏好并持久化，优先级高于系统默认。 */
  function selectModel(apiId: string): void {
    explicitApiId.value = apiId
    selectedApiId.value = apiId
    persistExplicitChoice(apiId)
  }

  /**
   * 拉取可用模型清单（官方 + 自定义）与自定义模型元数据。
   * 两个接口并行请求；任一失败都保留本地兜底，不阻塞聊天。
   */
  async function loadAvailableModels(): Promise<void> {
    try {
      const [modelsRes, customRes] = await Promise.all([
        aiChatApi.listAvailableModels(),
        aiChatApi.listCustomModels().catch(() => null)
      ])
      if (modelsRes.success && Array.isArray(modelsRes.data) && modelsRes.data.length > 0) {
        serverModels.value = modelsRes.data.filter(
          (model) => Boolean(model?.apiId) && Boolean(model?.label)
        )
      }
      if (customRes?.success && Array.isArray(customRes.data)) {
        customModels.value = customRes.data
      }
    } catch {
      // Keep the safe local fallback when the service is starting or unavailable.
    } finally {
      modelsLoaded.value = true
      // 清单更新后立即收敛一次，避免依赖 watcher 的下一 tick 才生效。
      resolveSelection()
    }
  }

  /** 创建自定义模型（学校共享需管理员；个人自建任意用户可建），成功后刷新清单。 */
  async function createCustomModel(
    payload: CreateCustomModelPayload
  ): Promise<AiCustomModelView> {
    const res = await aiChatApi.createCustomModel(payload)
    if (!res.success || !res.data) {
      throw new Error(res.message || '创建失败')
    }
    await loadAvailableModels()
    // 新建后自动选中，并记为显式偏好（优先级高于系统默认）。
    selectModel(res.data.apiId)
    return res.data
  }

  /** 测试连接：用提供的地址/密钥/模型名发一条 ping，不落库。 */
  async function testCustomModel(
    payload: TestCustomModelPayload
  ): Promise<TestCustomModelResult> {
    const res = await aiChatApi.testCustomModel(payload)
    if (!res.success || !res.data) {
      // 后端测试失败时也会返回 success=true + data.success=false；这里仅处理 HTTP 层失败。
      return { success: false, message: res.message || '测试失败' }
    }
    return res.data
  }

  /** 更新自定义模型（USER 仅本人；TENANT 仅管理员），成功后刷新清单。 */
  async function updateCustomModel(
    id: number,
    payload: UpdateCustomModelPayload
  ): Promise<AiCustomModelView> {
    const res = await aiChatApi.updateCustomModel(id, payload)
    if (!res.success || !res.data) {
      throw new Error(res.message || '保存失败')
    }
    await loadAvailableModels()
    return res.data
  }

  /** 删除自定义模型（USER 仅本人；TENANT 仅管理员），成功后刷新清单。 */
  async function deleteCustomModel(id: number): Promise<void> {
    const res = await aiChatApi.deleteCustomModel(id)
    if (!res.success) {
      throw new Error(res.message || '删除失败')
    }
    await loadAvailableModels()
  }

  /**
   * 重置租户级状态（服务端可用模型清单 + 自定义模型元数据），用于切换租户 / 登出。
   *
   * <p>{@code serverModels} / {@code customModels} 是按租户从服务端拉取的，不同学校可用
   * 的模型不同；切换租户后必须清空并标记未加载，迫使新租户重新拉取自己的模型清单，
   * 否则上一个学校的模型会残留短暂可见（跨租户数据残留）。</p>
   *
   * <p>{@code selectedApiId} 是用户级偏好（类似主题），持久化在 localStorage，
   * 跨租户保留合理，不清空。</p>
   */
  function resetTenantState(): void {
    serverModels.value = []
    customModels.value = []
    modelsLoaded.value = false
  }

  return {
    selectedApiId,
    selectedLabel,
    serverModels,
    customModels,
    modelsLoaded,
    allOptions,
    selectModel,
    loadAvailableModels,
    createCustomModel,
    updateCustomModel,
    testCustomModel,
    deleteCustomModel,
    resetTenantState
  }
})

registerTenantScopedReset('aiModelPreference', () =>
  useAiModelPreferenceStore().resetTenantState()
)
